import React, { useMemo, useState } from 'react';
import './ArchiveBrowser.css';
import { getPuzzleId, getPuzzleDate, getEasternParts, todayPuzzleId } from '../utils/puzzleId';
import { ARCHIVE_START } from '../hooks/usePuzzleDateParam';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * Month calendar of past puzzles.
 *
 * A calendar rather than a list: dailies are date-shaped, it is compact on a
 * phone, and played-versus-unplayed reads at a glance instead of needing a
 * label per row.
 *
 * No new endpoint — the player's full history already loads on mount. The
 * lookup set must be string-keyed, because puzzle_id comes back from Postgres
 * as TEXT and comparing it against a computed Number silently never matches.
 * That exact mistake is what kept the streak reading zero for the app's whole
 * history.
 */
const ArchiveBrowser = ({ userPuzzleHistory, currentPuzzleId, onSelect, onClose }) => {
  const today = todayPuzzleId();
  const startDate = getPuzzleDate(ARCHIVE_START);
  const { year: todayYear, month: todayMonth } = getEasternParts();

  const focus = getPuzzleDate(currentPuzzleId || today);
  const [view, setView] = useState({
    year: focus.getUTCFullYear(),
    month: focus.getUTCMonth() + 1, // 1-based
  });

  const playedIds = useMemo(() => {
    const rows = userPuzzleHistory?.userPuzzleHistory;
    if (!Array.isArray(rows)) return new Map();
    const map = new Map();
    rows.forEach((entry) => {
      // Prefer a real daily solve over an archive replay for the label.
      const key = String(entry.puzzle_id);
      const existing = map.get(key);
      if (!existing || (existing.context === 'archive' && entry.context !== 'archive')) {
        map.set(key, entry);
      }
    });
    return map;
  }, [userPuzzleHistory]);

  const atStart = view.year === startDate.getUTCFullYear() && view.month === startDate.getUTCMonth() + 1;
  const atEnd = view.year === todayYear && view.month === todayMonth;

  const step = (delta) => {
    setView((v) => {
      const m = v.month + delta;
      if (m < 1) return { year: v.year - 1, month: 12 };
      if (m > 12) return { year: v.year + 1, month: 1 };
      return { ...v, month: m };
    });
  };

  const days = useMemo(() => {
    const first = new Date(Date.UTC(view.year, view.month - 1, 1, 12));
    const leading = first.getUTCDay();
    const count = new Date(Date.UTC(view.year, view.month, 0, 12)).getUTCDate();

    const cells = Array.from({ length: leading }, () => null);
    for (let d = 1; d <= count; d++) {
      const date = new Date(Date.UTC(view.year, view.month - 1, d, 12));
      const id = getPuzzleId(date);
      const entry = playedIds.get(String(id));
      cells.push({
        day: d,
        id,
        available: id >= ARCHIVE_START && id <= today,
        played: Boolean(entry),
        gaveUp: entry?.gave_up === true,
        isCurrent: String(id) === String(currentPuzzleId || today),
      });
    }
    return cells;
  }, [view, playedIds, today, currentPuzzleId]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content archive-modal" onClick={(e) => e.stopPropagation()}>
        <div className="archive-header">
          <button
            type="button"
            className="archive-nav"
            onClick={() => step(-1)}
            disabled={atStart}
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="archive-month">{MONTHS[view.month - 1]} {view.year}</span>
          <button
            type="button"
            className="archive-nav"
            onClick={() => step(1)}
            disabled={atEnd}
            aria-label="Next month"
          >
            ›
          </button>
        </div>

        <div className="archive-grid archive-weekdays">
          {DAY_INITIALS.map((d, i) => <span key={i}>{d}</span>)}
        </div>

        <div className="archive-grid">
          {days.map((cell, i) =>
            cell === null ? (
              <span key={`pad-${i}`} />
            ) : (
              <button
                key={cell.id}
                type="button"
                className={`archive-day${cell.played ? ' played' : ''}${cell.isCurrent ? ' current' : ''}`}
                disabled={!cell.available}
                onClick={() => onSelect(cell.id)}
                title={cell.played ? (cell.gaveUp ? 'Gave up' : 'Solved') : undefined}
              >
                {cell.day}
              </button>
            )
          )}
        </div>

        <p className="archive-note">
          The archive starts {MONTHS[startDate.getUTCMonth()]} {startDate.getUTCDate()},{' '}
          {startDate.getUTCFullYear()}. Archive puzzles aren&apos;t ranked.
        </p>

        <div className="navigation-buttons">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ArchiveBrowser;
